import React, { useEffect, useMemo, useState } from 'react';
import api from '../api';
import PageHeader from '../components/PageHeader';
import BotSelector from '../components/BotSelector';
import { Avatar, Bar, Ic, Spinner, StatCard, fmtNum, useWidth } from '../components/ui';

/* Analitik — yalnızca gerçek veriden (/analytics/:botId) üretilen grafikler */

const COL = { in: '#6ab3f3', out: '#56c26a', users: '#a695e7', apps: '#e5a443', tickets: '#ef5b5b' };
const SERIES = {
  in: { key: 'in', label: 'Gelen', color: COL.in },
  out: { key: 'out', label: 'Giden', color: COL.out },
  users: { key: 'users', label: 'Yeni kullanıcı', color: COL.users },
  apps: { key: 'apps', label: 'Başvuru', color: COL.apps },
  tickets: { key: 'tickets', label: 'Ticket', color: COL.tickets },
};
const WEEKDAYS = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];

const CSS = `
.an-grid { display:grid; grid-template-columns:repeat(auto-fit, minmax(min(100%, 200px), 1fr)); gap:10px; margin-bottom:16px; }
.an-two { display:grid; grid-template-columns:minmax(0,1.6fr) minmax(0,1fr); gap:14px; }
.an-legend { display:flex; gap:14px; flex-wrap:wrap; margin-top:10px; }
.an-legend span { display:inline-flex; align-items:center; gap:6px; font-size:12.5px; color:var(--text2); }
.an-legend i { width:10px; height:10px; border-radius:3px; display:inline-block; }
.an-tip { position:absolute; pointer-events:none; background:#1f2936; border:1px solid var(--border); border-radius:10px; padding:8px 10px; font-size:12.5px; box-shadow:var(--shadow-md); z-index:5; min-width:130px; }
.an-tip .r { display:flex; align-items:center; gap:6px; justify-content:space-between; }
.an-tip .r b { font-weight:700; }
.an-head { display:flex; align-items:center; justify-content:space-between; gap:10px; flex-wrap:wrap; margin-bottom:14px; }
@media (max-width: 1000px) { .an-two { grid-template-columns:minmax(0,1fr); } }
@media (max-width: 480px) { .an-tabs .tab svg { display:none; } .an-tabs .tab { padding:10px 11px 9px; } }
@media (max-width: 768px) { .an-grid { grid-template-columns:repeat(2, minmax(0,1fr)); gap:8px; } .an-grid > :last-child:nth-child(odd) { grid-column:1 / -1; } .an-tw { max-height:none !important; overflow:visible !important; } .an-tw table.rtable tr { background:var(--surface2); } }
`;

/* ── yardımcılar ── */
const dkey = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const n = v => parseInt(v || 0, 10) || 0;
/* okunaklı eksen: 0 ve tam sayı adımlar (ör. 0-100-200-300-400) */
function niceScale(v) {
  let best = null;
  [4, 5, 3].forEach(n => {
    const raw = Math.max(v, 1) / n;
    const p = 10 ** Math.floor(Math.log10(raw));
    const f = raw / p;
    const step = Math.max(1, Math.ceil((f <= 1 ? 1 : f <= 2 ? 2 : f <= 5 ? 5 : 10) * p));
    const max = step * n;
    if (!best || max < best.max) best = { max, ticks: Array.from({ length: n + 1 }, (_, i) => i * step) };
  });
  return best;
}
const short = v => (v >= 1000000 ? (v / 1000000).toFixed(1).replace('.0', '') + 'M' : v >= 10000 ? Math.round(v / 1000) + 'B' : fmtNum(v));

function buildDays(rows, from, count) {
  const map = {};
  rows.forEach(r => { const k = dkey(new Date(r.date)); map[k] = r; });
  const out = [];
  for (let i = 0; i < count; i++) {
    const d = new Date(from); d.setDate(from.getDate() + i);
    const r = map[dkey(d)] || {};
    out.push({ d, label: d.toLocaleDateString('tr-TR', { day: '2-digit', month: 'short' }), in: n(r.messages_in), out: n(r.messages_out), users: n(r.new_users), apps: n(r.new_applications), tickets: n(r.new_tickets) });
  }
  return out;
}
function totals(days) {
  const t = days.reduce((a, d) => ({ in: a.in + d.in, out: a.out + d.out, users: a.users + d.users, apps: a.apps + d.apps, tickets: a.tickets + d.tickets }), { in: 0, out: 0, users: 0, apps: 0, tickets: 0 });
  t.msg = t.in + t.out;
  return t;
}
function delta(cur, prev) {
  if (!prev) return null;
  return Math.round(((cur - prev) / prev) * 100);
}
function DeltaSub({ v }) {
  if (v == null) return null;
  const up = v >= 0;
  return <><Ic n={up ? 'trendUp' : 'trendDown'} s={14} />{up ? '+' : ''}{v}% öncekine göre</>;
}

/* ── Çizgi grafik (gerçek piksel genişliği, üzerine gelince değerler) ── */
function LineChart({ data, series, height = 220 }) {
  const [ref, w] = useWidth();
  const [hover, setHover] = useState(null);
  const pad = { t: 12, r: 12, b: 26, l: 36 };
  const W = Math.max(w, 10), H = height;
  const cw = W - pad.l - pad.r, ch = H - pad.t - pad.b;
  const { max, ticks } = niceScale(Math.max(1, ...data.flatMap(d => series.map(s => d[s.key]))));
  const x = i => pad.l + (data.length > 1 ? (i / (data.length - 1)) * cw : cw / 2);
  const y = v => pad.t + ch - (v / max) * ch;
  const step = Math.max(1, Math.ceil(data.length / Math.max(2, Math.floor(cw / 62))));
  function move(e) {
    const r = e.currentTarget.getBoundingClientRect();
    const px = (e.touches ? e.touches[0].clientX : e.clientX) - r.left;
    const i = Math.round(((px - pad.l) / cw) * (data.length - 1));
    setHover(Math.max(0, Math.min(data.length - 1, i)));
  }
  const hv = hover != null ? data[hover] : null;
  return (
    <div ref={ref} style={{ position: 'relative', width: '100%' }}>
      {w > 0 && (
        <svg width={W} height={H} style={{ display: 'block', touchAction: 'pan-y' }} onMouseMove={move} onMouseLeave={() => setHover(null)} onTouchStart={move} onTouchMove={move} onTouchEnd={() => setHover(null)}>
          <defs>
            {series.map(s => (
              <linearGradient key={s.key} id={'g-' + s.key} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={s.color} stopOpacity="0.28" /><stop offset="100%" stopColor={s.color} stopOpacity="0" />
              </linearGradient>
            ))}
          </defs>
          {ticks.map(v => (
            <g key={v}>
              <line x1={pad.l} x2={W - pad.r} y1={y(v)} y2={y(v)} stroke={v === 0 ? '#2b3a4a' : '#1f2b38'} strokeDasharray={v === 0 ? '' : '3 4'} />
              <text x={pad.l - 6} y={y(v) + 4} textAnchor="end" fontSize="11" fill="#7f91a4">{short(v)}</text>
            </g>
          ))}
          {data.map((d, i) => (data.length - 1 - i) % step === 0 && (
            <text key={i} x={x(i)} y={H - 6} textAnchor={i === 0 && data.length > 1 ? 'start' : i === data.length - 1 && data.length > 1 ? 'end' : 'middle'} fontSize="11" fill="#7f91a4">{d.label}</text>
          ))}
          {series.map(s => {
            const pts = data.map((d, i) => `${x(i)},${y(d[s.key])}`).join(' ');
            const area = `M${x(0)},${y(0)} ` + data.map((d, i) => `L${x(i)},${y(d[s.key])}`).join(' ') + ` L${x(data.length - 1)},${y(0)} Z`;
            return (
              <g key={s.key}>
                <path d={area} fill={`url(#g-${s.key})`} />
                <polyline points={pts} fill="none" stroke={s.color} strokeWidth="2.2" strokeLinejoin="round" strokeLinecap="round" />
              </g>
            );
          })}
          {hv && (
            <g>
              <line x1={x(hover)} x2={x(hover)} y1={pad.t} y2={pad.t + ch} stroke="#3a4b5e" />
              {series.map(s => <circle key={s.key} cx={x(hover)} cy={y(hv[s.key])} r="4" fill={s.color} stroke="#17212b" strokeWidth="2" />)}
            </g>
          )}
        </svg>
      )}
      {hv && (
        <div className="an-tip" style={{ left: Math.min(Math.max(x(hover) - 70, 0), W - 150), top: 0 }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>{hv.d.toLocaleDateString('tr-TR', { weekday: 'short', day: 'numeric', month: 'long' })}</div>
          {series.map(s => <div key={s.key} className="r"><span style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--text2)' }}><i style={{ width: 8, height: 8, borderRadius: 2, background: s.color, display: 'inline-block' }} />{s.label}</span><b>{fmtNum(hv[s.key])}</b></div>)}
        </div>
      )}
    </div>
  );
}

/* ── Sütun grafik (gruplu) ── */
function BarChart({ data, series, height = 200, labelKey = 'label' }) {
  const [ref, w] = useWidth();
  const [hover, setHover] = useState(null);
  const pad = { t: 12, r: 8, b: 26, l: 36 };
  const W = Math.max(w, 10), H = height;
  const cw = W - pad.l - pad.r, ch = H - pad.t - pad.b;
  const { max, ticks } = niceScale(Math.max(1, ...data.flatMap(d => series.map(s => d[s.key]))));
  const slot = cw / Math.max(1, data.length);
  const gap = Math.min(10, slot * 0.25);
  const bw = Math.max(2, (slot - gap) / series.length);
  const y = v => pad.t + ch - (v / max) * ch;
  const step = Math.max(1, Math.ceil(data.length / Math.max(2, Math.floor(cw / 46))));
  const hv = hover != null ? data[hover] : null;
  return (
    <div ref={ref} style={{ position: 'relative', width: '100%' }}>
      {w > 0 && (
        <svg width={W} height={H} style={{ display: 'block' }} onMouseLeave={() => setHover(null)}>
          {ticks.map(v => (
            <g key={v}>
              <line x1={pad.l} x2={W - pad.r} y1={y(v)} y2={y(v)} stroke={v === 0 ? '#2b3a4a' : '#1f2b38'} strokeDasharray={v === 0 ? '' : '3 4'} />
              <text x={pad.l - 6} y={y(v) + 4} textAnchor="end" fontSize="11" fill="#7f91a4">{short(v)}</text>
            </g>
          ))}
          {data.map((d, i) => (
            <g key={i} onMouseEnter={() => setHover(i)} onClick={() => setHover(h => (h === i ? null : i))} style={{ cursor: 'default' }}>
              <rect x={pad.l + i * slot} y={pad.t} width={slot} height={ch} fill={hover === i ? 'rgba(255,255,255,.04)' : 'transparent'} />
              {series.map((s, si) => {
                const h = (d[s.key] / max) * ch;
                return <rect key={s.key} x={pad.l + i * slot + gap / 2 + si * bw} y={pad.t + ch - Math.max(h, d[s.key] ? 2 : 0)} width={Math.max(1, bw - (series.length > 1 ? 1.5 : 0))} height={Math.max(h, d[s.key] ? 2 : 0)} rx={Math.min(4, bw / 3)} fill={s.color} opacity={hover == null || hover === i ? 0.95 : 0.55} />;
              })}
              {(i % step === 0) && <text x={pad.l + i * slot + slot / 2} y={H - 6} textAnchor="middle" fontSize="11" fill="#7f91a4">{d[labelKey]}</text>}
            </g>
          ))}
        </svg>
      )}
      {hv && (
        <div className="an-tip" style={{ left: Math.min(Math.max(pad.l + hover * slot + slot / 2 - 70, 0), W - 150), top: 0 }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>{hv.full || hv[labelKey]}</div>
          {series.map(s => <div key={s.key} className="r"><span style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--text2)' }}><i style={{ width: 8, height: 8, borderRadius: 2, background: s.color, display: 'inline-block' }} />{s.label}</span><b>{fmtNum(hv[s.key])}</b></div>)}
        </div>
      )}
    </div>
  );
}

function Donut({ parts, size = 132, center, sub }) {
  const total = parts.reduce((s, p) => s + p.value, 0);
  const r = size / 2 - 10, c = 2 * Math.PI * r;
  let off = 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
      <svg width={size} height={size} style={{ flexShrink: 0 }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#242f3d" strokeWidth="14" />
        {total > 0 && parts.map(p => {
          const len = (p.value / total) * c;
          const el = <circle key={p.label} cx={size / 2} cy={size / 2} r={r} fill="none" stroke={p.color} strokeWidth="14" strokeDasharray={`${len} ${c - len}`} strokeDashoffset={-off} transform={`rotate(-90 ${size / 2} ${size / 2})`} />;
          off += len;
          return el;
        })}
        <text x={size / 2} y={size / 2 - 2} textAnchor="middle" fontSize="20" fontWeight="700" fill="#f5f5f5">{center}</text>
        <text x={size / 2} y={size / 2 + 16} textAnchor="middle" fontSize="11" fill="#7f91a4">{sub}</text>
      </svg>
      <div style={{ flex: 1, minWidth: 140, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {parts.map(p => (
          <div key={p.label}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--text2)' }}><i style={{ width: 9, height: 9, borderRadius: 3, background: p.color, display: 'inline-block' }} />{p.label}</span>
              <b>{fmtNum(p.value)} <span style={{ color: 'var(--text3)', fontWeight: 500 }}>· %{total ? Math.round((p.value / total) * 100) : 0}</span></b>
            </div>
            <Bar pct={total ? (p.value / total) * 100 : 0} color={p.color} h={5} />
          </div>
        ))}
      </div>
    </div>
  );
}

function Legend({ series }) {
  return <div className="an-legend">{series.map(s => <span key={s.key}><i style={{ background: s.color }} />{s.label}</span>)}</div>;
}

export default function Analytics() {
  const [bots, setBots] = useState([]);
  const [selBot, setSelBot] = useState(null);
  const [period, setPeriod] = useState('7');
  const [tab, setTab] = useState('overview');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cmp, setCmp] = useState(null);
  const [cmpLoading, setCmpLoading] = useState(false);
  const [stamp, setStamp] = useState(0);

  useEffect(() => { api.get('/bots').then(r => { setBots(r.data || []); if (r.data?.length) setSelBot(r.data[0]); }).catch(() => {}); }, []);
  useEffect(() => { if (selBot) load(); }, [selBot, period, stamp]);
  useEffect(() => { setCmp(null); }, [period, stamp]);
  useEffect(() => { if (tab === 'comparison' && !cmp && bots.length) loadComparison(); }, [tab, cmp, bots]);

  async function load() {
    setLoading(true);
    try {
      const res = await api.get(`/analytics/${selBot.id}`, { params: { days: Number(period) * 2 } });
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (e) { setRows([]); }
    setLoading(false);
  }
  async function loadComparison() {
    setCmpLoading(true);
    const list = await Promise.all(bots.map(b => api.get(`/analytics/${b.id}`, { params: { days: period } })
      .then(r => ({ bot: b, t: totals(buildDays(Array.isArray(r.data) ? r.data : [], startOf(Number(period)), Number(period))) }))
      .catch(() => ({ bot: b, t: totals([]) }))));
    setCmp(list.sort((a, b) => b.t.msg - a.t.msg));
    setCmpLoading(false);
  }

  const P = Number(period);
  const { days, prevDays } = useMemo(() => {
    const from = startOf(P);
    const prevFrom = new Date(from); prevFrom.setDate(from.getDate() - P);
    return { days: buildDays(rows, from, P), prevDays: buildDays(rows, prevFrom, P) };
  }, [rows, P]);
  const t = totals(days), pt = totals(prevDays);
  const hasPrev = pt.msg + pt.users + pt.apps + pt.tickets > 0;
  const conv = t.users ? ((t.apps / t.users) * 100).toFixed(1) : '0';
  const busiest = days.reduce((b, d) => (d.in + d.out > (b ? b.in + b.out : -1) ? d : b), null);
  const weekday = useMemo(() => {
    const acc = WEEKDAYS.map((l, i) => ({ label: l, full: ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar'][i], in: 0, out: 0, c: 0 }));
    days.forEach(d => { const i = (d.d.getDay() + 6) % 7; acc[i].in += d.in; acc[i].out += d.out; acc[i].c += 1; });
    return acc.map(a => ({ ...a, in: a.c ? Math.round(a.in / a.c) : 0, out: a.c ? Math.round(a.out / a.c) : 0 }));
  }, [days]);
  let cum = 0;
  const growth = days.map(d => ({ ...d, total: (cum += d.users) }));
  const empty = !loading && t.msg + t.users + t.apps + t.tickets === 0;

  const tabs = [['overview', 'Genel', 'chart'], ['messages', 'Mesajlar', 'chat'], ['users', 'Kullanıcılar', 'users'], ['comparison', 'Botlar', 'bot']];

  return (
    <div className="page-enter">
      <style>{CSS}</style>
      <PageHeader
        title="Analitik"
        subtitle={selBot ? `${selBot.name} · son ${P} gün` : 'Bot performans analizi'}
        actions={<>
          <div className="seg">
            {[['7', '7 gün'], ['30', '30 gün'], ['90', '90 gün']].map(([v, l]) => <button key={v} className={period === v ? 'on' : ''} onClick={() => setPeriod(v)}>{l}</button>)}
          </div>
          <button className="btn btn-ghost btn-sm" onClick={() => setStamp(Date.now())} title="Yenile"><Ic n="refresh" s={16} />Yenile</button>
        </>}
        botSelector={tab !== 'comparison' ? <BotSelector bots={bots} selected={selBot} onSelect={setSelBot} /> : null}
      />

      <div className="tabs an-tabs" style={{ marginBottom: 16 }}>
        {tabs.map(([k, l, ic]) => <button key={k} className={'tab' + (tab === k ? ' active' : '')} onClick={() => setTab(k)} style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}><Ic n={ic} s={17} />{l}</button>)}
      </div>

      {tab !== 'comparison' && loading && <div className="card" style={{ display: 'flex', justifyContent: 'center', padding: 50 }}><Spinner /></div>}
      {tab !== 'comparison' && empty && (
        <div className="card"><div className="empty-state"><div className="icon"><Ic n="chart" s={44} /></div><p>Bu dönem için veri yok</p><p className="text-sm" style={{ marginTop: 4 }}>Bot mesaj aldıkça grafikler burada oluşur</p></div></div>
      )}

      {/* ═══ GENEL ═══ */}
      {tab === 'overview' && !loading && !empty && (
        <>
          <div className="an-grid">
            <StatCard icon="chat" label="Toplam mesaj" value={fmtNum(t.msg)} color={COL.in} bg="var(--info-s)" sub={hasPrev ? <DeltaSub v={delta(t.msg, pt.msg)} /> : `${fmtNum(Math.round(t.msg / P))} / gün`} subColor={hasPrev && delta(t.msg, pt.msg) < 0 ? 'var(--danger)' : 'var(--success)'} />
            <StatCard icon="userPlus" label="Yeni kullanıcı" value={fmtNum(t.users)} color={COL.users} bg="var(--purple-s)" sub={hasPrev ? <DeltaSub v={delta(t.users, pt.users)} /> : null} subColor={delta(t.users, pt.users) < 0 ? 'var(--danger)' : 'var(--success)'} />
            <StatCard icon="clipboard" label="Başvuru" value={fmtNum(t.apps)} color={COL.apps} bg="var(--warning-s)" sub={hasPrev ? <DeltaSub v={delta(t.apps, pt.apps)} /> : null} subColor={delta(t.apps, pt.apps) < 0 ? 'var(--danger)' : 'var(--success)'} />
            <StatCard icon="ticket" label="Ticket" value={fmtNum(t.tickets)} color={COL.tickets} bg="var(--danger-s)" sub={hasPrev ? <DeltaSub v={delta(t.tickets, pt.tickets)} /> : null} subColor={delta(t.tickets, pt.tickets) > 0 ? 'var(--warning)' : 'var(--success)'} />
            <StatCard icon="target" label="Başvuru oranı" value={`%${conv}`} color={COL.out} bg="var(--success-s)" sub="başvuru / yeni kullanıcı" />
          </div>

          <div className="chart-container" style={{ marginBottom: 14 }}>
            <div className="an-head" style={{ marginBottom: 6 }}>
              <div><div className="chart-title">Günlük aktivite</div><div className="chart-subtitle" style={{ marginBottom: 0 }}>Gelen / giden mesaj ve yeni kullanıcı</div></div>
              {busiest && busiest.in + busiest.out > 0 && <span className="chip" style={{ background: 'var(--accent-s)', color: 'var(--link)' }}><Ic n="star" s={13} />En yoğun: {busiest.label} · {fmtNum(busiest.in + busiest.out)}</span>}
            </div>
            <LineChart data={days} series={[SERIES.in, SERIES.out, SERIES.users]} height={240} />
            <Legend series={[SERIES.in, SERIES.out, SERIES.users]} />
          </div>

          <div className="an-two">
            <div className="chart-container">
              <div className="chart-title">Başvuru & ticket</div>
              <div className="chart-subtitle">Günlük adet</div>
              <BarChart data={days} series={[SERIES.apps, SERIES.tickets]} height={200} />
              <Legend series={[SERIES.apps, SERIES.tickets]} />
            </div>
            <div className="chart-container">
              <div className="chart-title">Mesaj dağılımı</div>
              <div className="chart-subtitle">Gelen ve giden oranı</div>
              <Donut parts={[{ label: 'Gelen', value: t.in, color: COL.in }, { label: 'Giden', value: t.out, color: COL.out }]} center={short(t.msg)} sub="mesaj" />
              <div className="divider" />
              <div className="kv-grid keep-grid" style={{ gridTemplateColumns: 'repeat(2, minmax(0,1fr))' }}>
                <div className="kv"><div className="k">Günlük ortalama</div><div className="v">{fmtNum(Math.round(t.msg / P))}</div></div>
                <div className="kv"><div className="k">Yanıt oranı</div><div className="v">%{t.in ? Math.min(999, Math.round((t.out / t.in) * 100)) : 0}</div></div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ═══ MESAJLAR ═══ */}
      {tab === 'messages' && !loading && !empty && (
        <>
          <div className="an-grid">
            <StatCard icon="inbox" label="Gelen mesaj" value={fmtNum(t.in)} color={COL.in} bg="var(--info-s)" sub={hasPrev ? <DeltaSub v={delta(t.in, pt.in)} /> : null} subColor={delta(t.in, pt.in) < 0 ? 'var(--danger)' : 'var(--success)'} />
            <StatCard icon="send" label="Giden mesaj" value={fmtNum(t.out)} color={COL.out} bg="var(--success-s)" sub={hasPrev ? <DeltaSub v={delta(t.out, pt.out)} /> : null} subColor={delta(t.out, pt.out) < 0 ? 'var(--danger)' : 'var(--success)'} />
            <StatCard icon="chart" label="Günlük ortalama" value={fmtNum(Math.round(t.msg / P))} color="var(--purple)" bg="var(--purple-s)" />
            <StatCard icon="star" label="En yoğun gün" value={busiest && busiest.in + busiest.out ? busiest.label : '—'} color={COL.apps} bg="var(--warning-s)" sub={busiest && busiest.in + busiest.out ? `${fmtNum(busiest.in + busiest.out)} mesaj` : null} />
          </div>
          <div className="an-two" style={{ marginBottom: 14 }}>
            <div className="chart-container">
              <div className="chart-title">Mesaj trendi</div>
              <div className="chart-subtitle">Gün gün gelen ve giden</div>
              <LineChart data={days} series={[SERIES.in, SERIES.out]} height={230} />
              <Legend series={[SERIES.in, SERIES.out]} />
            </div>
            <div className="chart-container">
              <div className="chart-title">Haftanın günleri</div>
              <div className="chart-subtitle">Gün başına ortalama mesaj</div>
              <BarChart data={weekday} series={[SERIES.in, SERIES.out]} height={230} />
              <Legend series={[SERIES.in, SERIES.out]} />
            </div>
          </div>
          <DailyTable days={days} />
        </>
      )}

      {/* ═══ KULLANICILAR ═══ */}
      {tab === 'users' && !loading && !empty && (
        <>
          <div className="an-grid">
            <StatCard icon="userPlus" label="Yeni kullanıcı" value={fmtNum(t.users)} color={COL.users} bg="var(--purple-s)" sub={hasPrev ? <DeltaSub v={delta(t.users, pt.users)} /> : null} subColor={delta(t.users, pt.users) < 0 ? 'var(--danger)' : 'var(--success)'} />
            <StatCard icon="calendar" label="Günlük ortalama" value={(t.users / P).toFixed(1).replace('.0', '')} color="var(--link)" bg="var(--accent-s)" />
            <StatCard icon="clipboard" label="Başvuru" value={fmtNum(t.apps)} color={COL.apps} bg="var(--warning-s)" />
            <StatCard icon="target" label="Başvuru oranı" value={`%${conv}`} color={COL.out} bg="var(--success-s)" sub="başvuru / yeni kullanıcı" />
          </div>
          <div className="an-two">
            <div className="chart-container">
              <div className="chart-title">Kümülatif büyüme</div>
              <div className="chart-subtitle">Dönem başından itibaren toplam yeni kullanıcı</div>
              <LineChart data={growth} series={[{ key: 'total', label: 'Toplam', color: COL.users }]} height={230} />
            </div>
            <div className="chart-container">
              <div className="chart-title">Günlük yeni kullanıcı</div>
              <div className="chart-subtitle">ve aynı gün gelen başvuru</div>
              <BarChart data={days} series={[SERIES.users, SERIES.apps]} height={230} />
              <Legend series={[SERIES.users, SERIES.apps]} />
            </div>
          </div>
        </>
      )}

      {/* ═══ KARŞILAŞTIRMA ═══ */}
      {tab === 'comparison' && (
        cmpLoading || !cmp ? <div className="card" style={{ display: 'flex', justifyContent: 'center', padding: 50 }}><Spinner /></div> : (
          <>
            <div className="chart-container" style={{ marginBottom: 14 }}>
              <div className="chart-title">Botlara göre mesaj · son {P} gün</div>
              <div className="chart-subtitle">Gelen ve giden mesaj toplamı</div>
              <BarChart data={cmp.slice(0, 12).map(c => ({ label: c.bot.name.length > 9 ? c.bot.name.slice(0, 8) + '…' : c.bot.name, full: c.bot.name, in: c.t.in, out: c.t.out }))} series={[SERIES.in, SERIES.out]} height={230} />
              <Legend series={[SERIES.in, SERIES.out]} />
            </div>
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <div className="sec-title"><span className="flex-center gap-8"><Ic n="trophy" s={18} c="var(--warning)" />Sıralama</span><span className="text-muted text-sm" style={{ fontWeight: 500 }}>mesaj hacmine göre</span></div>
              <div className="list">
                {cmp.map((c, i) => {
                  const top = cmp[0]?.t.msg || 1;
                  return (
                    <div key={c.bot.id} className="list-row click" onClick={() => { setSelBot(c.bot); setTab('overview'); }}>
                      <div style={{ width: 26, textAlign: 'center', fontWeight: 700, color: i < 3 ? ['#f5c542', '#c0c8d2', '#d39a62'][i] : 'var(--text3)' }}>{i + 1}</div>
                      <Avatar id={c.bot.id} name={c.bot.name} size={40} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="flex-between" style={{ gap: 8 }}>
                          <span className="truncate" style={{ fontWeight: 600 }}>{c.bot.name}</span>
                          <span style={{ fontWeight: 700, flexShrink: 0 }}>{fmtNum(c.t.msg)}</span>
                        </div>
                        <div style={{ margin: '5px 0 4px' }}><Bar pct={(c.t.msg / top) * 100} color={i === 0 ? 'var(--link)' : 'var(--accent)'} h={5} /></div>
                        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 12.5, color: 'var(--text3)' }}>
                          <span><b style={{ color: COL.users }}>{fmtNum(c.t.users)}</b> yeni</span>
                          <span><b style={{ color: COL.apps }}>{fmtNum(c.t.apps)}</b> başvuru</span>
                          <span><b style={{ color: COL.tickets }}>{fmtNum(c.t.tickets)}</b> ticket</span>
                          <span>%{c.t.users ? ((c.t.apps / c.t.users) * 100).toFixed(1) : '0'} oran</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )
      )}
    </div>
  );
}

function startOf(P) {
  const d = new Date(); d.setHours(0, 0, 0, 0); d.setDate(d.getDate() - (P - 1));
  return d;
}

function DailyTable({ days }) {
  return (
    <div className="card rtable-wrap" style={{ padding: 0, overflow: 'hidden' }}>
      <div className="sec-title"><span className="flex-center gap-8"><Ic n="calendar" s={18} c="var(--link)" />Günlük detay</span></div>
      <div className="table-wrap an-tw" style={{ maxHeight: 420, overflowY: 'auto' }}>
        <table className="rtable">
          <thead><tr><th>Tarih</th><th>Gelen</th><th>Giden</th><th>Yeni kullanıcı</th><th>Başvuru</th><th>Ticket</th></tr></thead>
          <tbody>
            {days.slice().reverse().map(d => (
              <tr key={d.label}>
                <td data-label="Tarih" className="full" style={{ fontWeight: 600 }}>{d.d.toLocaleDateString('tr-TR', { weekday: 'short', day: 'numeric', month: 'short' })}</td>
                <td data-label="Gelen" style={{ color: COL.in, fontWeight: 600 }}>{fmtNum(d.in)}</td>
                <td data-label="Giden" style={{ color: COL.out, fontWeight: 600 }}>{fmtNum(d.out)}</td>
                <td data-label="Yeni kullanıcı" style={{ color: COL.users, fontWeight: 600 }}>{fmtNum(d.users)}</td>
                <td data-label="Başvuru" style={{ color: COL.apps, fontWeight: 600 }}>{fmtNum(d.apps)}</td>
                <td data-label="Ticket" style={{ color: COL.tickets, fontWeight: 600 }}>{fmtNum(d.tickets)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
